import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { StickyNote, Trash2 } from "lucide-react";

const ROLE = { admin: "Safer Highways", customer: "Company", terminal_manager: "Terminal manager" };

export function NotesPanel({ targetType, targetId, compact = false }) {
  const [rows, setRows] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/notes", { params: { target_type: targetType, target_id: targetId } }).then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, [targetType, targetId]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = async () => { if (!body.trim()) return; setBusy(true); try { await api.post("/notes", { target_type: targetType, target_id: targetId, body }); setBody(""); load(); toast.success("Note added"); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const del = async (n) => { try { await api.delete(`/notes/${n.id}`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className={`${compact ? "" : "col-span-2"} rounded-md border border-amber-200 bg-amber-50/40`} data-testid={`notes-panel-${targetType}`}>
      <div className="px-3 py-2 border-b border-amber-200 text-xs font-semibold flex items-center gap-1 text-amber-900"><StickyNote className="h-3.5 w-3.5" />Notes ({rows.length}) <span className="font-normal text-amber-700">· visible to the company, terminal managers and Safer Highways</span></div>
      <div className="p-3 space-y-2">
        {rows.length === 0 && <div className="text-xs text-slate-500">No notes yet.</div>}
        {rows.map((n) => (
          <div key={n.id} className="rounded bg-white border p-2 text-sm flex gap-2" data-testid={`note-${n.id}`}>
            <div className="flex-1 min-w-0"><div className="whitespace-pre-wrap">{n.body}</div><div className="text-[11px] text-slate-500 mt-1">{n.author} · {ROLE[n.author_role] || n.author_role}{n.terminal_name ? ` (${n.terminal_name})` : ""} · {new Date(n.created_at).toLocaleString()}</div></div>
            <button type="button" className="text-slate-400 hover:text-rose-600 shrink-0" onClick={() => del(n)} aria-label="Delete note" data-testid={`note-delete-${n.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <div className="flex gap-2 items-end">
          <Textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a note — e.g. out on medical leave until 10/15, unit in shop…" className="bg-white text-sm" data-testid="note-input" />
          <Button size="sm" onClick={add} disabled={busy || !body.trim()} className="bg-amber-600 hover:bg-amber-700" data-testid="note-add">Add</Button>
        </div>
      </div>
    </div>
  );
}
